import type { CustomSvgData } from './ProjectDocument.js'

export const MAX_CUSTOM_SVG_BYTES = 50 * 1_024
export const MAX_CUSTOM_SVG_NAME_LENGTH = 256
export const MAX_SVG_VIEWBOX_EXTENT = 100_000
export const MAX_SVG_SOURCE_ELEMENT_COUNT = 4_096
export const MAX_SVG_EXPANDED_ELEMENT_COUNT = 4_096
export const MAX_SVG_NESTING_DEPTH = 64

const SVG_DATA_URL_PREFIX = 'data:image/svg+xml;base64,'
const MAX_BASE64_PAYLOAD_LENGTH = Math.ceil(MAX_CUSTOM_SVG_BYTES / 3) * 4
export const MAX_CUSTOM_SVG_DATA_URL_LENGTH = (
  SVG_DATA_URL_PREFIX.length + MAX_BASE64_PAYLOAD_LENGTH
)

const XML_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_.:-]*/
const XML_ENTITY_PATTERN = /&(?:amp|lt|gt|quot|apos|#\d+|#x[\dA-Fa-f]+);/g
const XML_DECLARATION_PATTERN = /^<\?xml\s+version=(?:"1\.[01]"|'1\.[01]')(?:\s+encoding=(?:"utf-8"|'utf-8'))?(?:\s+standalone=(?:"(?:yes|no)"|'(?:yes|no)'))?\s*\?>$/i
const STATIC_PAINT_PATTERN = /^(?:none|currentColor|context-fill|context-stroke|transparent|inherit|initial|unset|#[\dA-Fa-f]{3}|#[\dA-Fa-f]{4}|#[\dA-Fa-f]{6}|#[\dA-Fa-f]{8}|[A-Za-z]+)$/i
const CSS_NUMBER_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/
const MAX_INLINE_STYLE_LENGTH = 8_192
const MAX_INLINE_STYLE_DECLARATIONS = 128
const MAX_INLINE_STYLE_PARENTHESIS_DEPTH = 16

function createSvgNamespace(separatorCode: number): string {
  return ['http:', '', 'www.w3.org', '2000', 'svg']
    .join(String.fromCharCode(separatorCode))
}

const LOCAL_PAINT_REFERENCE_ATTRIBUTES = new Set([
  'fill',
  'stroke',
  'clip-path',
  'mask',
])

const ALLOWED_ELEMENTS = new Set([
  'svg',
  'g',
  'defs',
  'symbol',
  'use',
  'path',
  'rect',
  'circle',
  'ellipse',
  'line',
  'polyline',
  'polygon',
  'text',
  'tspan',
  'textPath',
  'pattern',
  'linearGradient',
  'radialGradient',
  'stop',
  'clipPath',
  'mask',
  'title',
  'desc',
  'metadata',
])

const TEXT_ELEMENTS = new Set([
  'title',
  'desc',
  'text',
  'tspan',
  'textPath',
  'metadata',
])

const VISIBLE_TEXT_ELEMENTS = new Set([
  'title',
  'desc',
  'text',
  'tspan',
  'textPath',
])

const REFERENCE_ELEMENTS = new Set([
  'use',
  'textPath',
  'pattern',
  'linearGradient',
  'radialGradient',
])

const ALLOWED_ATTRIBUTES = new Set([
  'xmlns',
  'xml:space',
  'version',
  'viewBox',
  'preserveAspectRatio',
  'width',
  'height',
  'x',
  'y',
  'x1',
  'x2',
  'y1',
  'y2',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'fx',
  'fy',
  'fr',
  'dx',
  'dy',
  'rotate',
  'd',
  'pathLength',
  'points',
  'transform',
  'id',
  'class',
  'fill',
  'fill-rule',
  'clip-rule',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-opacity',
  'opacity',
  'color',
  'vector-effect',
  'paint-order',
  'shape-rendering',
  'offset',
  'stop-color',
  'stop-opacity',
  'gradientUnits',
  'gradientTransform',
  'spreadMethod',
  'patternUnits',
  'patternContentUnits',
  'patternTransform',
  'clipPathUnits',
  'maskUnits',
  'maskContentUnits',
  'clip-path',
  'mask',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'font-stretch',
  'text-anchor',
  'dominant-baseline',
  'alignment-baseline',
  'baseline-shift',
  'letter-spacing',
  'word-spacing',
  'text-decoration',
  'direction',
  'unicode-bidi',
  'writing-mode',
  'textLength',
  'lengthAdjust',
  'startOffset',
  'method',
  'spacing',
  'side',
  'href',
])

const INLINE_STYLE_PROPERTIES = new Set([
  'fill',
  'fill-rule',
  'clip-rule',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-opacity',
  'opacity',
  'color',
  'vector-effect',
  'paint-order',
  'shape-rendering',
  'stop-color',
  'stop-opacity',
  'clip-path',
  'mask',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'font-stretch',
  'text-anchor',
  'dominant-baseline',
  'alignment-baseline',
  'baseline-shift',
  'letter-spacing',
  'word-spacing',
  'text-decoration',
  'direction',
  'unicode-bidi',
  'writing-mode',
])

type UnknownRecord = Record<string, unknown>

interface SvgAttribute {
  name: string
  value: string
  decodedValue: string
  quote: '"' | "'"
}

interface StyleDeclaration {
  attribute: SvgAttribute
  important: boolean
  order: number
}

interface OpeningTag {
  name: string
  attributes: SvgAttribute[]
  selfClosing: boolean
  canonicalSource: string
}

interface ParsedElement {
  name: string
  children: number[]
  referenceIds: string[]
}

interface StackEntry {
  name: string
  nodeIndex: number | null
  discarded: boolean
}

interface ParsedSvg {
  xml: string
  nodes: ParsedElement[]
  ids: Map<string, number>
  rootIndex: number
}

interface TraversalFrame {
  nodeIndex: number
  nextNeighbor: number
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isValidXmlCodePoint(codePoint: number): boolean {
  return codePoint === 0x09
    || codePoint === 0x0a
    || codePoint === 0x0d
    || (codePoint >= 0x20 && codePoint <= 0xd7ff)
    || (codePoint >= 0xe000 && codePoint <= 0xfffd)
    || (codePoint >= 0x1_0000 && codePoint <= 0x10_ffff)
}

function isXmlNameStartCodePoint(codePoint: number): boolean {
  return codePoint === 0x3a
    || codePoint === 0x5f
    || (codePoint >= 0x41 && codePoint <= 0x5a)
    || (codePoint >= 0x61 && codePoint <= 0x7a)
    || (codePoint >= 0xc0 && codePoint <= 0xd6)
    || (codePoint >= 0xd8 && codePoint <= 0xf6)
    || (codePoint >= 0xf8 && codePoint <= 0x2ff)
    || (codePoint >= 0x370 && codePoint <= 0x37d)
    || (codePoint >= 0x37f && codePoint <= 0x1fff)
    || (codePoint >= 0x200c && codePoint <= 0x200d)
    || (codePoint >= 0x2070 && codePoint <= 0x218f)
    || (codePoint >= 0x2c00 && codePoint <= 0x2fef)
    || (codePoint >= 0x3001 && codePoint <= 0xd7ff)
    || (codePoint >= 0xf900 && codePoint <= 0xfdcf)
    || (codePoint >= 0xfdf0 && codePoint <= 0xfffd)
    || (codePoint >= 0x1_0000 && codePoint <= 0xe_ffff)
}

function isXmlNameCodePoint(codePoint: number): boolean {
  return isXmlNameStartCodePoint(codePoint)
    || codePoint === 0x2d
    || codePoint === 0x2e
    || (codePoint >= 0x30 && codePoint <= 0x39)
    || codePoint === 0xb7
    || (codePoint >= 0x300 && codePoint <= 0x36f)
    || (codePoint >= 0x203f && codePoint <= 0x2040)
}

function isValidXmlName(value: string): boolean {
  let first = true
  for (const character of value) {
    const codePoint = character.codePointAt(0)
    if (
      codePoint === undefined
      || (first ? !isXmlNameStartCodePoint(codePoint) : !isXmlNameCodePoint(codePoint))
    ) {
      return false
    }
    first = false
  }

  return !first
}

function parseInternalFragment(value: string): string | null {
  if (!value.startsWith('#')) {
    return null
  }

  const id = value.slice(1)
  return isValidXmlName(id) ? id : null
}

function trimReferenceWhitespace(value: string): string {
  return value.replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, '')
}

function parseInternalUrlReference(value: string): string | null {
  const source = trimReferenceWhitespace(value)
  if (
    source.length < 6
    || source.slice(0, 4).toLowerCase() !== 'url('
    || !source.endsWith(')')
  ) {
    return null
  }

  let fragment = trimReferenceWhitespace(source.slice(4, -1))
  const quote = fragment[0]
  if (quote === '"' || quote === "'") {
    if (fragment.length < 2 || fragment[fragment.length - 1] !== quote) {
      return null
    }
    fragment = fragment.slice(1, -1)
  }

  return parseInternalFragment(fragment)
}

function parseProcessingInstructionTarget(instruction: string): string | null {
  if (!instruction.startsWith('<?') || !instruction.endsWith('?>')) {
    return null
  }

  const body = instruction.slice(2, -2)
  const whitespaceIndex = body.search(/[ \t\r\n]/)
  const target = whitespaceIndex < 0 ? body : body.slice(0, whitespaceIndex)
  if (target.length === 0 || target.toLowerCase() === 'xml') {
    return null
  }

  return isValidXmlName(target) ? target : null
}

function decodeXmlEntities(value: string): string | null {
  if (value.replace(XML_ENTITY_PATTERN, '').includes('&')) {
    return null
  }

  let valid = true
  const decoded = value.replace(XML_ENTITY_PATTERN, (entity) => {
    if (entity === '&amp;') return '&'
    if (entity === '&lt;') return '<'
    if (entity === '&gt;') return '>'
    if (entity === '&quot;') return '"'
    if (entity === '&apos;') return "'"

    const hexadecimal = entity.startsWith('&#x')
    const numericText = entity.slice(hexadecimal ? 3 : 2, -1)
    const codePoint = Number.parseInt(numericText, hexadecimal ? 16 : 10)
    if (!Number.isFinite(codePoint) || !isValidXmlCodePoint(codePoint)) {
      valid = false
      return ''
    }
    return String.fromCodePoint(codePoint)
  })

  return valid ? decoded : null
}

function decodeSvgDataUrl(dataUrl: string): string | null {
  if (
    dataUrl.length > MAX_CUSTOM_SVG_DATA_URL_LENGTH
    || !dataUrl.startsWith(SVG_DATA_URL_PREFIX)
  ) {
    return null
  }

  const payload = dataUrl.slice(SVG_DATA_URL_PREFIX.length)
  if (
    payload.length === 0
    || payload.length > MAX_BASE64_PAYLOAD_LENGTH
    || payload.length % 4 !== 0
    || !/^[A-Za-z0-9+/]*={0,2}$/.test(payload)
  ) {
    return null
  }

  try {
    const binary = atob(payload)
    if (binary.length > MAX_CUSTOM_SVG_BYTES || btoa(binary) !== payload) {
      return null
    }

    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index)
    }

    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return null
  }
}

function encodeSvgDataUrl(svgXml: string): string | null {
  const bytes = new TextEncoder().encode(svgXml)
  if (bytes.byteLength > MAX_CUSTOM_SVG_BYTES) {
    return null
  }

  let binary = ''
  const chunkSize = 8_192
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
  }

  const dataUrl = `${SVG_DATA_URL_PREFIX}${btoa(binary)}`
  return dataUrl.length <= MAX_CUSTOM_SVG_DATA_URL_LENGTH ? dataUrl : null
}

function parseCssNumber(value: string): number | null {
  if (!CSS_NUMBER_PATTERN.test(value)) {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function isNumberInRange(value: string, minimum: number, maximum: number): boolean {
  const parsed = parseCssNumber(value)
  return parsed !== null && parsed >= minimum && parsed <= maximum
}

function isPercentageInRange(value: string, minimum: number, maximum: number): boolean {
  return value.endsWith('%')
    && isNumberInRange(value.slice(0, -1), minimum, maximum)
}

function isSafeRgbChannel(value: string): boolean {
  return value.endsWith('%')
    ? isPercentageInRange(value, 0, 100)
    : isNumberInRange(value, 0, 255)
}

function isSafeAlpha(value: string): boolean {
  return value.endsWith('%')
    ? isPercentageInRange(value, 0, 100)
    : isNumberInRange(value, 0, 1)
}

function parseFunctionalPaintArguments(value: string): {
  channels: string[]
  alpha: string | null
} | null {
  if (!/^[\s+.,%/\d-]+$/.test(value)) {
    return null
  }

  if (value.includes(',')) {
    if (value.includes('/')) {
      return null
    }
    const components = value.split(',').map((component) => component.trim())
    if (components.some((component) => component.length === 0)) {
      return null
    }
    return components.length === 3 || components.length === 4
      ? { channels: components.slice(0, 3), alpha: components[3] ?? null }
      : null
  }

  const slashParts = value.split('/')
  if (slashParts.length > 2) {
    return null
  }
  const channels = (slashParts[0] ?? '').trim().split(/\s+/).filter(Boolean)
  const alphaPart = slashParts[1]?.trim() ?? null
  if (channels.length !== 3 || alphaPart === '') {
    return null
  }
  if (alphaPart !== null && /\s/.test(alphaPart)) {
    return null
  }

  return { channels, alpha: alphaPart }
}

function isSafeFunctionalPaint(value: string): boolean {
  const match = value.match(/^(rgb|rgba|hsl|hsla)\((.*)\)$/i)
  const functionName = match?.[1]?.toLowerCase()
  const argumentSource = match?.[2]
  if (!functionName || argumentSource === undefined) {
    return false
  }

  const parsed = parseFunctionalPaintArguments(argumentSource)
  if (!parsed || (parsed.alpha !== null && !isSafeAlpha(parsed.alpha))) {
    return false
  }

  if (functionName === 'rgb' || functionName === 'rgba') {
    return parsed.channels.every(isSafeRgbChannel)
  }

  const [hue, saturation, lightness] = parsed.channels
  return hue !== undefined
    && saturation !== undefined
    && lightness !== undefined
    && isNumberInRange(hue, 0, 360)
    && isPercentageInRange(saturation, 0, 100)
    && isPercentageInRange(lightness, 0, 100)
}

function isSafePaint(value: string, allowLocalReference: boolean): boolean {
  if (allowLocalReference && parseInternalUrlReference(value) !== null) {
    return true
  }

  const trimmed = value.trim()
  return STATIC_PAINT_PATTERN.test(trimmed) || isSafeFunctionalPaint(trimmed)
}

function isSafeAttribute(elementName: string, attribute: SvgAttribute): boolean {
  if (!ALLOWED_ATTRIBUTES.has(attribute.name)) {
    return false
  }

  const lowerName = attribute.name.toLowerCase()
  const value = attribute.decodedValue.trim()
  if (
    lowerName.startsWith('on')
    || lowerName === 'style'
    || value.includes('\\')
  ) {
    return false
  }

  if (attribute.name === 'xmlns') {
    return value === createSvgNamespace(47)
  }

  if (attribute.name === 'xml:space') {
    return value === 'default' || value === 'preserve'
  }

  if (attribute.name === 'href') {
    return REFERENCE_ELEMENTS.has(elementName) && parseInternalFragment(value) !== null
  }

  if (attribute.name === 'fill' || attribute.name === 'stroke') {
    return isSafePaint(attribute.decodedValue, true)
  }

  if (attribute.name === 'fill-rule' || attribute.name === 'clip-rule') {
    return value === 'nonzero' || value === 'evenodd' || value === 'inherit'
  }

  if (attribute.name === 'stop-color' || attribute.name === 'color') {
    return isSafePaint(attribute.decodedValue, false)
  }

  if (attribute.name === 'clip-path' || attribute.name === 'mask') {
    return value === 'none' || parseInternalUrlReference(attribute.decodedValue) !== null
  }

  return !/(?:javascript|vbscript|data|https?):|\/\//i.test(value)
}

function parseAttributes(source: string): SvgAttribute[] | null {
  const attributes: SvgAttribute[] = []
  const names = new Set<string>()
  let cursor = 0

  while (cursor < source.length) {
    while (cursor < source.length && /\s/.test(source[cursor] ?? '')) {
      cursor += 1
    }
    if (cursor >= source.length) {
      break
    }

    const nameMatch = source.slice(cursor).match(XML_NAME_PATTERN)
    if (!nameMatch) {
      return null
    }
    const name = nameMatch[0]
    cursor += name.length

    while (cursor < source.length && /\s/.test(source[cursor] ?? '')) {
      cursor += 1
    }
    if (source[cursor] !== '=') {
      return null
    }
    cursor += 1

    while (cursor < source.length && /\s/.test(source[cursor] ?? '')) {
      cursor += 1
    }
    const quote = source[cursor]
    if (quote !== '"' && quote !== "'") {
      return null
    }
    cursor += 1

    const valueEnd = source.indexOf(quote, cursor)
    if (valueEnd < 0) {
      return null
    }
    const value = source.slice(cursor, valueEnd)
    const decodedValue = decodeXmlEntities(value)
    if (value.includes('<') || decodedValue === null || names.has(name)) {
      return null
    }
    names.add(name)
    attributes.push({ name, value, decodedValue, quote })
    cursor = valueEnd + 1
    if (cursor < source.length && !/\s/.test(source[cursor] ?? '')) {
      return null
    }
  }

  return attributes
}

function escapeXmlAttributeValue(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
}

function escapeXmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function splitInlineStyleDeclarations(css: string): string[] | null {
  const declarations: string[] = []
  let quote: '"' | "'" | null = null
  let parenthesisDepth = 0
  let declarationStart = 0

  for (let index = 0; index < css.length; index += 1) {
    const character = css[index]
    if (quote) {
      if (character === quote) {
        quote = null
      }
      continue
    }

    if (character === '"' || character === "'") {
      quote = character
    } else if (character === '(') {
      parenthesisDepth += 1
      if (parenthesisDepth > MAX_INLINE_STYLE_PARENTHESIS_DEPTH) {
        return null
      }
    } else if (character === ')') {
      parenthesisDepth -= 1
      if (parenthesisDepth < 0) {
        return null
      }
    } else if (character === ';' && parenthesisDepth === 0) {
      declarations.push(css.slice(declarationStart, index))
      declarationStart = index + 1
      if (declarations.length > MAX_INLINE_STYLE_DECLARATIONS) {
        return null
      }
    }
  }

  if (quote !== null || parenthesisDepth !== 0) {
    return null
  }

  declarations.push(css.slice(declarationStart))
  if (declarations[declarations.length - 1]?.trim() === '') {
    declarations.pop()
  }
  return declarations.length <= MAX_INLINE_STYLE_DECLARATIONS
    && declarations.every((declaration) => declaration.trim().length > 0)
    ? declarations
    : null
}

function findDeclarationColon(declaration: string): number | null {
  let quote: '"' | "'" | null = null
  let parenthesisDepth = 0
  let colonIndex = -1

  for (let index = 0; index < declaration.length; index += 1) {
    const character = declaration[index]
    if (quote) {
      if (character === quote) {
        quote = null
      }
      continue
    }

    if (character === '"' || character === "'") {
      quote = character
    } else if (character === '(') {
      parenthesisDepth += 1
    } else if (character === ')') {
      parenthesisDepth -= 1
    } else if (character === ':' && parenthesisDepth === 0) {
      if (colonIndex >= 0) {
        return null
      }
      colonIndex = index
    }
  }

  return colonIndex > 0 ? colonIndex : null
}

function parseStyleImportance(value: string): {
  value: string
  important: boolean
} | null {
  let quote: '"' | "'" | null = null
  let parenthesisDepth = 0
  let bangIndex = -1

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index]
    if (quote) {
      if (character === quote) {
        quote = null
      }
      continue
    }

    if (character === '"' || character === "'") {
      quote = character
    } else if (character === '(') {
      parenthesisDepth += 1
    } else if (character === ')') {
      parenthesisDepth -= 1
    } else if (character === '!') {
      if (parenthesisDepth !== 0 || bangIndex >= 0) {
        return null
      }
      bangIndex = index
    }
  }

  if (bangIndex < 0) {
    return value.length > 0 ? { value, important: false } : null
  }

  const importance = value.slice(bangIndex).trim()
  const unmarkedValue = value.slice(0, bangIndex).trimEnd()
  return /^!\s*important$/i.test(importance) && unmarkedValue.length > 0
    ? { value: unmarkedValue, important: true }
    : null
}

function parseInlineStyle(
  elementName: string,
  styleAttribute: SvgAttribute,
): SvgAttribute[] | null {
  const css = styleAttribute.decodedValue.trim()
  if (css.length === 0) {
    return []
  }
  if (
    css.length > MAX_INLINE_STYLE_LENGTH
    || css.includes('\\')
    || css.includes('/*')
    || css.includes('*/')
    || css.includes('@')
    || css.includes('{')
    || css.includes('}')
  ) {
    return null
  }

  const declarations = splitInlineStyleDeclarations(css)
  if (!declarations) {
    return null
  }

  const winners = new Map<string, StyleDeclaration>()
  for (let order = 0; order < declarations.length; order += 1) {
    const declaration = declarations[order]
    if (declaration === undefined) {
      return null
    }

    const colonIndex = findDeclarationColon(declaration)
    if (colonIndex === null) {
      return null
    }
    const property = declaration.slice(0, colonIndex).trim().toLowerCase()
    const importance = parseStyleImportance(declaration.slice(colonIndex + 1).trim())
    if (
      !/^[a-z][a-z0-9-]*$/.test(property)
      || !INLINE_STYLE_PROPERTIES.has(property)
      || !importance
      || /(?:var|env|calc|attr|expression)\s*\(/i.test(importance.value)
      || (
        /url\s*\(/i.test(importance.value)
        && property !== 'fill'
        && property !== 'stroke'
        && property !== 'clip-path'
        && property !== 'mask'
      )
    ) {
      return null
    }

    const attribute: SvgAttribute = {
      name: property,
      value: escapeXmlAttributeValue(importance.value),
      decodedValue: importance.value,
      quote: '"',
    }
    if (!isSafeAttribute(elementName, attribute)) {
      return null
    }

    const existing = winners.get(property)
    const hasHigherPriority = importance.important && existing?.important === false
    const hasEqualPriority = existing?.important === importance.important
    if (!existing || hasHigherPriority || hasEqualPriority) {
      winners.set(property, { attribute, important: importance.important, order })
    }
  }

  return [...winners.values()]
    .sort((left, right) => left.order - right.order)
    .map((winner) => winner.attribute)
}

function canonicalizeInlineStyle(
  elementName: string,
  attributes: SvgAttribute[],
): SvgAttribute[] | null {
  const styleAttribute = attributes.find((attribute) => attribute.name === 'style')
  if (!styleAttribute) {
    return attributes
  }

  const styleAttributes = parseInlineStyle(elementName, styleAttribute)
  if (!styleAttributes) {
    return null
  }

  const canonical = attributes.filter((attribute) => attribute.name !== 'style')
  const attributeIndexes = new Map(
    canonical.map((attribute, index) => [attribute.name, index]),
  )
  for (const attribute of styleAttributes) {
    const existingIndex = attributeIndexes.get(attribute.name)
    if (existingIndex === undefined) {
      attributeIndexes.set(attribute.name, canonical.length)
      canonical.push(attribute)
    } else {
      canonical[existingIndex] = attribute
    }
  }

  return canonical
}

function stripNonVisualEditorAttributes(attributes: SvgAttribute[]): SvgAttribute[] {
  return attributes.filter((attribute) => {
    if (/^data-/i.test(attribute.name) || attribute.name.startsWith('xmlns:')) {
      return false
    }

    return !attribute.name.includes(':')
      || attribute.name === 'xlink:href'
      || attribute.name === 'xml:space'
  })
}

function canonicalizeLocalHref(
  elementName: string,
  attributes: SvgAttribute[],
): SvgAttribute[] | null {
  const hrefAttributes = attributes.filter(
    (attribute) => attribute.name === 'href' || attribute.name === 'xlink:href',
  )
  if (hrefAttributes.length === 0) {
    return attributes
  }
  if (
    !REFERENCE_ELEMENTS.has(elementName)
    || hrefAttributes.some(
      (attribute) => parseInternalFragment(attribute.decodedValue.trim()) === null,
    )
  ) {
    return null
  }

  const winner = hrefAttributes.find((attribute) => attribute.name === 'href')
    ?? hrefAttributes[0]
  if (!winner) {
    return null
  }

  const insertionIndex = attributes.findIndex(
    (attribute) => attribute.name === 'href' || attribute.name === 'xlink:href',
  )
  const canonical = attributes.filter(
    (attribute) => attribute.name !== 'href' && attribute.name !== 'xlink:href',
  )
  const winnerId = parseInternalFragment(winner.decodedValue.trim())
  if (winnerId === null) {
    return null
  }
  const value = `#${winnerId}`
  canonical.splice(insertionIndex, 0, {
    name: 'href',
    value: escapeXmlAttributeValue(value),
    decodedValue: value,
    quote: '"',
  })
  return canonical
}

function canonicalizeLocalPaintAndXmlSpace(attributes: SvgAttribute[]): SvgAttribute[] {
  return attributes.map((attribute) => {
    if (attribute.name === 'xml:space') {
      const value = attribute.decodedValue.trim()
      return {
        name: attribute.name,
        value: escapeXmlAttributeValue(value),
        decodedValue: value,
        quote: '"',
      }
    }

    if (!LOCAL_PAINT_REFERENCE_ATTRIBUTES.has(attribute.name)) {
      return attribute
    }
    const referenceId = parseInternalUrlReference(attribute.decodedValue)
    if (referenceId === null) {
      return attribute
    }

    const value = `url(#${referenceId})`
    return {
      name: attribute.name,
      value: escapeXmlAttributeValue(value),
      decodedValue: value,
      quote: '"',
    }
  })
}

function canonicalizeOpeningTagAttributes(
  elementName: string,
  attributes: SvgAttribute[],
): SvgAttribute[] | null {
  const withoutEditorMetadata = stripNonVisualEditorAttributes(attributes)
  const withCanonicalHref = canonicalizeLocalHref(elementName, withoutEditorMetadata)
  if (withCanonicalHref === null) {
    return null
  }

  const withCanonicalStyle = canonicalizeInlineStyle(elementName, withCanonicalHref)
  return withCanonicalStyle === null
    ? null
    : canonicalizeLocalPaintAndXmlSpace(withCanonicalStyle)
}

function serializeOpeningTag(
  name: string,
  attributes: readonly SvgAttribute[],
  selfClosing: boolean,
): string {
  const serializedAttributes = attributes
    .map((attribute) => ` ${attribute.name}=${attribute.quote}${attribute.value}${attribute.quote}`)
    .join('')
  return `${name}${serializedAttributes}${selfClosing ? '/' : ''}`
}

function parseOpeningTag(source: string): OpeningTag | null {
  const trimmed = source.trim()
  const selfClosing = trimmed.endsWith('/')
  const body = selfClosing ? trimmed.slice(0, -1).trimEnd() : trimmed
  const nameMatch = body.match(XML_NAME_PATTERN)
  if (!nameMatch) {
    return null
  }

  const name = nameMatch[0]
  const attributeSource = body.slice(name.length)
  if (attributeSource.length > 0 && !/^\s/.test(attributeSource)) {
    return null
  }

  const parsedAttributes = parseAttributes(attributeSource)
  const attributes = parsedAttributes === null
    ? null
    : name === 'metadata'
      ? parsedAttributes
      : canonicalizeOpeningTagAttributes(name, parsedAttributes)
  if (
    !attributes
    || !ALLOWED_ELEMENTS.has(name)
    || (
      name !== 'metadata'
      && !attributes.every((attribute) => isSafeAttribute(name, attribute))
    )
  ) {
    return null
  }

  return {
    name,
    attributes,
    selfClosing,
    canonicalSource: serializeOpeningTag(name, attributes, selfClosing),
  }
}

function findTagEnd(svgXml: string, tagStart: number): number {
  let quote: '"' | "'" | null = null

  for (let index = tagStart + 1; index < svgXml.length; index += 1) {
    const character = svgXml[index]
    if (quote) {
      if (character === quote) {
        quote = null
      }
      continue
    }

    if (character === '"' || character === "'") {
      quote = character
    } else if (character === '<') {
      return -1
    } else if (character === '>') {
      return index
    }
  }

  return -1
}

function textIsSafe(text: string, parentElement: string | undefined): boolean {
  if (text.includes(']]>')) {
    return false
  }
  if (text.trim().length === 0) {
    return true
  }

  return parentElement !== undefined
    && TEXT_ELEMENTS.has(parentElement)
    && !text.includes('<')
    && decodeXmlEntities(text) !== null
}

function stripXmlDeclaration(svgXml: string): string | null {
  let candidate = svgXml.trim()
  if (candidate.startsWith('<?xml')) {
    const declarationEnd = candidate.indexOf('?>')
    if (declarationEnd < 0) {
      return null
    }
    const declaration = candidate.slice(0, declarationEnd + 2)
    if (!XML_DECLARATION_PATTERN.test(declaration)) {
      return null
    }
    candidate = candidate.slice(declarationEnd + 2).trimStart()
  }

  return candidate
}

function containsInvalidXmlCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0)
    if (codePoint === undefined || !isValidXmlCodePoint(codePoint)) {
      return true
    }
  }

  return false
}

function itemAt<TValue>(values: readonly TValue[], index: number): TValue | undefined {
  return values[index]
}

function lastStackEntry(stack: readonly StackEntry[]): StackEntry | undefined {
  return itemAt(stack, stack.length - 1)
}

function analyzeElement(openingTag: OpeningTag): {
  id: string | null
  referenceIds: string[]
} | null {
  const idAttribute = openingTag.attributes.find((attribute) => attribute.name === 'id')
  const id = idAttribute?.decodedValue ?? null
  if (id !== null && !isValidXmlName(id)) {
    return null
  }

  const hrefAttributes = openingTag.attributes.filter((attribute) => attribute.name === 'href')
  if (hrefAttributes.length > 1) {
    return null
  }
  if ((openingTag.name === 'use' || openingTag.name === 'textPath') && hrefAttributes.length !== 1) {
    return null
  }

  const referenceIds = new Set<string>()
  for (const attribute of openingTag.attributes) {
    const value = attribute.decodedValue.trim()
    if (attribute.name === 'href') {
      const referenceId = parseInternalFragment(value)
      if (!referenceId) {
        return null
      }
      referenceIds.add(referenceId)
      continue
    }

    if (
      attribute.name === 'fill'
      || attribute.name === 'stroke'
      || attribute.name === 'clip-path'
      || attribute.name === 'mask'
    ) {
      const referenceId = parseInternalUrlReference(value)
      if (referenceId) {
        referenceIds.add(referenceId)
      }
    }
  }

  return { id, referenceIds: [...referenceIds] }
}

function parseSvgXml(svgXml: string): ParsedSvg | null {
  const candidate = stripXmlDeclaration(svgXml)
  if (!candidate || containsInvalidXmlCharacter(candidate)) {
    return null
  }

  const stack: StackEntry[] = []
  const nodes: ParsedElement[] = []
  const ids = new Map<string, number>()
  const output: string[] = []
  let cursor = 0
  let rootIndex = -1
  let rootSeen = false
  let rootClosed = false
  let characterDataTail = ''

  const appendCharacterData = (value: string): boolean => {
    const joined = `${characterDataTail}${value}`
    if (joined.includes(']]>')) {
      return false
    }

    output.push(value)
    characterDataTail = joined.endsWith(']]')
      ? ']]'
      : joined.endsWith(']')
        ? ']'
        : ''
    return true
  }

  const appendMarkup = (value: string): void => {
    output.push(value)
    characterDataTail = ''
  }

  while (cursor < candidate.length) {
    const tagStart = candidate.indexOf('<', cursor)
    const textEnd = tagStart < 0 ? candidate.length : tagStart
    const text = candidate.slice(cursor, textEnd)
    const parent = lastStackEntry(stack)
    if (!textIsSafe(text, parent?.name)) {
      return null
    }
    if (
      parent
      && !parent.discarded
      && !rootClosed
      && !appendCharacterData(text)
    ) {
      return null
    }

    if (tagStart < 0) {
      cursor = candidate.length
      break
    }

    if (candidate.startsWith('<!--', tagStart)) {
      const commentEnd = candidate.indexOf('-->', tagStart + 4)
      const comment = commentEnd < 0 ? '' : candidate.slice(tagStart + 4, commentEnd)
      if (commentEnd < 0 || comment.includes('--') || comment.endsWith('-')) {
        return null
      }
      cursor = commentEnd + 3
      continue
    }

    if (rootClosed) {
      return null
    }

    if (candidate.startsWith('<![CDATA[', tagStart)) {
      const cdataEnd = candidate.indexOf(']]>', tagStart + 9)
      const cdataParent = lastStackEntry(stack)
      if (cdataEnd < 0 || !cdataParent) {
        return null
      }

      if (!cdataParent.discarded) {
        if (!VISIBLE_TEXT_ELEMENTS.has(cdataParent.name)) {
          return null
        }
        const escapedCdata = escapeXmlText(candidate.slice(tagStart + 9, cdataEnd))
        if (!appendCharacterData(escapedCdata)) {
          return null
        }
      }
      cursor = cdataEnd + 3
      continue
    }

    if (candidate.startsWith('<?', tagStart)) {
      const instructionEnd = candidate.indexOf('?>', tagStart + 2)
      const instruction = instructionEnd < 0
        ? ''
        : candidate.slice(tagStart, instructionEnd + 2)
      const target = parseProcessingInstructionTarget(instruction)
      const instructionParent = lastStackEntry(stack)
      if (
        instructionEnd < 0
        || !target
        || !instructionParent?.discarded
      ) {
        return null
      }

      cursor = instructionEnd + 2
      continue
    }

    const tagEnd = findTagEnd(candidate, tagStart)
    if (tagEnd < 0) {
      return null
    }

    const tagSource = candidate.slice(tagStart + 1, tagEnd)
    if (tagSource.startsWith('!')) {
      return null
    }

    if (tagSource.startsWith('/')) {
      const closingMatch = tagSource.match(/^\/([A-Za-z_][A-Za-z0-9_.:-]*)\s*$/)
      const closingName = closingMatch?.[1]
      const closingEntry = stack.pop()
      if (!closingName || !closingEntry || closingEntry.name !== closingName) {
        return null
      }
      if (!closingEntry.discarded) {
        appendMarkup(`<${tagSource}>`)
      }
      if (stack.length === 0) {
        rootClosed = true
      }
    } else {
      const openingTag = parseOpeningTag(tagSource)
      const parentEntry = lastStackEntry(stack)
      if (
        !openingTag
        || (rootSeen && stack.length === 0)
      ) {
        return null
      }

      if (!rootSeen) {
        if (openingTag.name !== 'svg') {
          return null
        }
        rootSeen = true
      }

      if (parentEntry?.discarded === true) {
        if (!openingTag.selfClosing) {
          stack.push({ name: openingTag.name, nodeIndex: null, discarded: true })
          if (stack.length > MAX_SVG_NESTING_DEPTH) {
            return null
          }
        }
        cursor = tagEnd + 1
        continue
      }

      if (openingTag.name === 'metadata') {
        if (stack.length === 0) {
          return null
        }
        if (!openingTag.selfClosing) {
          stack.push({ name: openingTag.name, nodeIndex: null, discarded: true })
          if (stack.length > MAX_SVG_NESTING_DEPTH) {
            return null
          }
        }
        cursor = tagEnd + 1
        continue
      }

      if (nodes.length >= MAX_SVG_SOURCE_ELEMENT_COUNT) {
        return null
      }
      const analysis = analyzeElement(openingTag)
      if (!analysis) {
        return null
      }

      const nodeIndex = nodes.length
      const node: ParsedElement = {
        name: openingTag.name,
        children: [],
        referenceIds: analysis.referenceIds,
      }
      nodes.push(node)
      if (analysis.id !== null) {
        if (ids.has(analysis.id)) {
          return null
        }
        ids.set(analysis.id, nodeIndex)
      }

      const parentNodeIndex = parentEntry?.nodeIndex
      if (parentNodeIndex !== undefined && parentNodeIndex !== null) {
        const parentNode = itemAt(nodes, parentNodeIndex)
        if (!parentNode) {
          return null
        }
        parentNode.children.push(nodeIndex)
      } else {
        rootIndex = nodeIndex
      }

      appendMarkup(`<${openingTag.canonicalSource}>`)
      if (openingTag.selfClosing) {
        if (stack.length === 0) {
          rootClosed = true
        }
      } else {
        stack.push({ name: openingTag.name, nodeIndex, discarded: false })
        if (stack.length > MAX_SVG_NESTING_DEPTH) {
          return null
        }
      }
    }

    cursor = tagEnd + 1
  }

  if (!rootSeen || !rootClosed || stack.length !== 0 || rootIndex < 0) {
    return null
  }

  return {
    xml: output.join('').trim(),
    nodes,
    ids,
    rootIndex,
  }
}

function buildExpansionGraph(parsed: ParsedSvg): number[][] | null {
  const adjacency = parsed.nodes.map((node) => [...node.children])
  for (let nodeIndex = 0; nodeIndex < parsed.nodes.length; nodeIndex += 1) {
    const node = itemAt(parsed.nodes, nodeIndex)
    const neighbors = itemAt(adjacency, nodeIndex)
    if (!node || !neighbors) {
      return null
    }

    for (const referenceId of node.referenceIds) {
      const targetIndex = parsed.ids.get(referenceId)
      if (targetIndex === undefined) {
        return null
      }
      neighbors.push(targetIndex)
    }
  }

  return adjacency
}

function topologicalPostorder(adjacency: readonly number[][]): number[] | null {
  const states = new Uint8Array(adjacency.length)
  const postorder: number[] = []

  for (let startIndex = 0; startIndex < adjacency.length; startIndex += 1) {
    if (states[startIndex] !== 0) {
      continue
    }

    states[startIndex] = 1
    const stack: TraversalFrame[] = [{ nodeIndex: startIndex, nextNeighbor: 0 }]
    while (stack.length > 0) {
      const frame = itemAt(stack, stack.length - 1)
      if (!frame) {
        return null
      }
      const neighbors = itemAt(adjacency, frame.nodeIndex)
      if (!neighbors) {
        return null
      }

      if (frame.nextNeighbor >= neighbors.length) {
        states[frame.nodeIndex] = 2
        postorder.push(frame.nodeIndex)
        stack.pop()
        continue
      }

      const neighbor = itemAt(neighbors, frame.nextNeighbor)
      frame.nextNeighbor += 1
      if (neighbor === undefined) {
        return null
      }
      if (states[neighbor] === 1) {
        return null
      }
      if (states[neighbor] === 0) {
        states[neighbor] = 1
        stack.push({ nodeIndex: neighbor, nextNeighbor: 0 })
      }
    }
  }

  return postorder
}

function isWithinExpansionBudget(parsed: ParsedSvg): boolean {
  const adjacency = buildExpansionGraph(parsed)
  if (!adjacency) {
    return false
  }
  const postorder = topologicalPostorder(adjacency)
  if (!postorder) {
    return false
  }

  const costs = new Uint32Array(parsed.nodes.length)
  for (const nodeIndex of postorder) {
    const neighbors = itemAt(adjacency, nodeIndex)
    if (!neighbors) {
      return false
    }

    let cost = 1
    for (const neighbor of neighbors) {
      cost += costs[neighbor] ?? 0
      if (cost > MAX_SVG_EXPANDED_ELEMENT_COUNT) {
        return false
      }
    }
    costs[nodeIndex] = cost
  }

  return (costs[parsed.rootIndex] ?? 0) <= MAX_SVG_EXPANDED_ELEMENT_COUNT
}

function finiteViewBoxNumber(value: unknown, minimum: number, maximum: number): number | null {
  return typeof value === 'number'
    && Number.isFinite(value)
    && value >= minimum
    && value <= maximum
    ? value
    : null
}

function containsControlCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0)
    if (codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f)) {
      return true
    }
  }

  return false
}

/**
 * Validates untrusted project/upload SVG data and returns a detached canonical
 * base64 data URL. The feature contract preserves static shapes, patterns,
 * text, gradients, masks, symbols and bounded local references only.
 */
export function validateCustomSvgData(value: unknown): CustomSvgData | null {
  if (!isRecord(value) || !isRecord(value.viewBox)) {
    return null
  }
  if (
    typeof value.dataUrl !== 'string'
    || typeof value.name !== 'string'
    || value.name.trim().length === 0
    || value.name.length > MAX_CUSTOM_SVG_NAME_LENGTH
    || containsControlCharacter(value.name)
  ) {
    return null
  }

  const x = finiteViewBoxNumber(value.viewBox.x, -MAX_SVG_VIEWBOX_EXTENT, MAX_SVG_VIEWBOX_EXTENT)
  const y = finiteViewBoxNumber(value.viewBox.y, -MAX_SVG_VIEWBOX_EXTENT, MAX_SVG_VIEWBOX_EXTENT)
  const width = finiteViewBoxNumber(value.viewBox.width, Number.MIN_VALUE, MAX_SVG_VIEWBOX_EXTENT)
  const height = finiteViewBoxNumber(value.viewBox.height, Number.MIN_VALUE, MAX_SVG_VIEWBOX_EXTENT)
  if (x === null || y === null || width === null || height === null) {
    return null
  }

  const decodedSvg = decodeSvgDataUrl(value.dataUrl)
  const parsedSvg = decodedSvg === null ? null : parseSvgXml(decodedSvg)
  if (!parsedSvg || !isWithinExpansionBudget(parsedSvg)) {
    return null
  }
  const dataUrl = encodeSvgDataUrl(parsedSvg.xml)
  if (dataUrl === null) {
    return null
  }

  return {
    dataUrl,
    name: value.name,
    viewBox: { x, y, width, height },
  }
}
